import { useEffect, useState } from "react";
import {
  ChevronDown,
  Upload,
  Users,
  Briefcase,
  User,
  Settings as SettingsIcon,
  Edit,
  Trash2,
  Music,
  Volume2,
} from "lucide-react";
import { toast } from "react-toastify";

import api from "../../services/api";
import tokenManager from "../../utils/tokenManager";
import { useUser } from "../../contexts/UserContext";
import { useLicense } from "../../hooks/useLicense";
import { useNavigate } from "react-router-dom";

import LicensePanel from "../../components/common/LicensePanel";
import InviteTeamModal from "../../components/common/InviteTeamModal";
import UserAccessCell from "../../components/common/UserAccessCell";

// ── Small reusable primitives ────────────────────────────────────────────────
const Toggle = ({ enabled, onChange }) => (
  <button
    onClick={() => onChange(!enabled)}
    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
      enabled ? "bg-[#7F56D9]" : "bg-gray-600"
    }`}
  >
    <span
      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
        enabled ? "translate-x-6" : "translate-x-1"
      }`}
    />
  </button>
);

const Dropdown = ({
  isOpen,
  setIsOpen,
  value,
  options,
  onChange,
  placeholder,
}) => (
  <div className="relative">
    <button
      onClick={() => setIsOpen(!isOpen)}
      className="w-full bg-[#1a1d23] border border-gray-600 rounded-lg px-3 py-2 text-left text-white focus:outline-none focus:border-[#7F56D9] flex items-center justify-between"
    >
      <span>{value || placeholder}</span>
      <ChevronDown
        className={`h-4 w-4 text-gray-400 transform transition-transform ${
          isOpen ? "rotate-180" : ""
        }`}
      />
    </button>
    {isOpen && (
      <div className="absolute z-20 mt-1 w-full bg-[#1a1d23] border border-gray-600 rounded-lg shadow-lg max-h-48 overflow-auto">
        {options.map((option) => (
          <button
            key={option}
            onClick={() => {
              onChange(option);
              setIsOpen(false);
            }}
            className="w-full text-left px-3 py-2 text-gray-300 hover:bg-gray-700 hover:text-white first:rounded-t-lg last:rounded-b-lg"
          >
            {option}
          </button>
        ))}
      </div>
    )}
  </div>
);

// ── Settings Page ────────────────────────────────────────────────────────────
function SettingsPage() {
  const [activeTab, setActiveTab] = useState("my-details");
  const { currentUser, refreshUser } = useUser();
  const { refresh: refreshLicense } = useLicense();
  const [isLoadingUser, setIsLoadingUser] = useState(true);
  const navigate = useNavigate();

  const isAdmin =
    currentUser?.role === "admin" || currentUser?.role === "super_admin";

  // ── Form state ─────────────────────────────────────────────────────────
  const [personalDetails, setPersonalDetails] = useState({
    name: "",
    email: "",
    profileImageUrl: "",
  });

  const [workspaceDetails, setWorkspaceDetails] = useState({
    companyName: "",
    workspaceUrl: "",
    logoUrl: "",
  });

  // ── Team ──────────────────────────────────────────────────────────────
  const [teamMembers, setTeamMembers] = useState([]);
  const [pendingInvitations, setPendingInvitations] = useState([]);
  const [isLoadingTeam, setIsLoadingTeam] = useState(true);
  const [showInviteModal, setShowInviteModal] = useState(false);

  // ── Voice preferences ─────────────────────────────────────────────────
  const [defaultVoice, setDefaultVoice] = useState("Jenny (en-GB)");
  const [defaultTone, setDefaultTone] = useState("Friendly");
  const [defaultModel, setDefaultModel] = useState("Studio v2");
  const [enableMusicLayer, setEnableMusicLayer] = useState(true);
  const [enableAutoNormalize, setEnableAutoNormalize] = useState(true);
  const [isVoiceOpen, setIsVoiceOpen] = useState(false);
  const [isToneOpen, setIsToneOpen] = useState(false);
  const [isModelOpen, setIsModelOpen] = useState(false);

  const voices = [
    "Jenny (en-GB)",
    "Ryan (en-GB)",
    "Guy (en-US)",
    "Aria (en-US)",
    "Clara (fr-FR)",
    "Hans (de-DE)",
  ];
  const tones = ["Neutral", "Friendly", "Professional", "Energetic", "Calm"];
  const models = ["Studio v1", "Studio v2", "Studio HQ"];

  // ── Tabs (Recording-Studio specific) ──────────────────────────────────
  const tabs = [
    { id: "my-details", label: "My details", icon: User },
    ...(isAdmin
      ? [
          { id: "workspace", label: "Workspace", icon: Briefcase },
          { id: "team", label: "Team", icon: Users },
        ]
      : []),
    { id: "voice-preferences", label: "Voice preferences", icon: Volume2 },
  ];

  // ── Effects ───────────────────────────────────────────────────────────

  // Fetch user on mount / user change
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        setIsLoadingUser(true);
        const userData = await api.getMe();

        setPersonalDetails({
          name: userData.user.name || "",
          email: userData.user.email || "",
          profileImageUrl: userData.user.profileImageUrl || "",
        });

        if (userData.user.company) {
          setWorkspaceDetails({
            companyName: userData.user.company.name || "",
            workspaceUrl: userData.user.company.workspaceUrl || "",
            logoUrl: userData.user.company.logoUrl || "",
          });
        }
      } catch (error) {
        console.error("Failed to fetch user data:", error);
        toast.error("Failed to load user data");
      } finally {
        setIsLoadingUser(false);
      }
    };

    if (currentUser) fetchUserData();
  }, [currentUser?.id]);

  // Fetch team on mount for admins
  useEffect(() => {
    const fetchTeamData = async () => {
      if (!isAdmin) {
        setIsLoadingTeam(false);
        return;
      }
      try {
        setIsLoadingTeam(true);
        const response = await api.getTeamMembers();
        setTeamMembers(response.users || []);
        setPendingInvitations(response.pendingInvitations || []);
      } catch (error) {
        console.error("Failed to fetch team data:", error);
        toast.error("Failed to load team data");
      } finally {
        setIsLoadingTeam(false);
      }
    };
    if (currentUser) fetchTeamData();
  }, [currentUser, isAdmin]);

  // ── Handlers ──────────────────────────────────────────────────────────

  const reloadTeam = async () => {
    try {
      const response = await api.getTeamMembers();
      setTeamMembers(response.users || []);
      setPendingInvitations(response.pendingInvitations || []);
      refreshLicense();
    } catch (error) {
      console.error("Failed to reload team:", error);
    }
  };

  const handleUpdatePersonalDetails = async () => {
    try {
      const response = await api.updatePersonalDetails({
        name: personalDetails.name,
        email: personalDetails.email,
      });
      tokenManager.setUser(response.user);
      refreshUser();
      toast.success("Personal details updated");
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.message || "Failed to update details");
    }
  };

  const handleUpdateWorkspaceDetails = async () => {
    try {
      await api.updateCompanyDetails({
        name: workspaceDetails.companyName,
        workspaceUrl: workspaceDetails.workspaceUrl,
      });
      refreshUser();
      toast.success("Workspace updated");
    } catch (error) {
      console.error(error);
      toast.error(
        error.response?.data?.message || "Failed to update workspace",
      );
    }
  };

  const handleProfileImageUpload = async (file) => {
    try {
      const formData = new FormData();
      formData.append("profileImage", file);
      const response = await api.uploadProfileImage(formData);

      setPersonalDetails((prev) => ({
        ...prev,
        profileImageUrl: response.profileImageUrl,
      }));

      const updatedUser = {
        ...currentUser,
        profileImageUrl: response.profileImageUrl,
      };
      tokenManager.setUser(updatedUser);
      refreshUser();

      toast.success("Profile image uploaded");
    } catch (error) {
      console.error(error);
      toast.error("Failed to upload profile image");
    }
  };

  const handleCompanyLogoUpload = async (file) => {
    try {
      const response = await api.uploadAsset(file);
      setWorkspaceDetails((prev) => ({ ...prev, logoUrl: response.url }));
      toast.success("Company logo uploaded");
    } catch (error) {
      console.error(error);
      toast.error("Failed to upload company logo");
    }
  };

  const handleUpdateRole = async (userId, newRole) => {
    try {
      await api.updateUserRole(userId, newRole);
      await reloadTeam();
      toast.success("Role updated");
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.message || "Failed to update role");
    }
  };

  const handleRemoveUser = async (userId) => {
    if (!window.confirm("Are you sure you want to remove this user?")) return;
    try {
      await api.removeUser(userId);
      await reloadTeam();
      toast.success("User removed");
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.message || "Failed to remove user");
    }
  };

  const handleResendInvitation = async (invitationId) => {
    try {
      await api.resendInvitation(invitationId);
      toast.success("Invitation resent");
    } catch (error) {
      console.error(error);
      toast.error(
        error.response?.data?.message || "Failed to resend invitation",
      );
    }
  };

  const handleCancelInvitation = async (invitationId) => {
    if (!window.confirm("Cancel this invitation?")) return;
    try {
      await api.cancelInvitation(invitationId);
      await reloadTeam();
      toast.success("Invitation cancelled");
    } catch (error) {
      console.error(error);
      toast.error(
        error.response?.data?.message || "Failed to cancel invitation",
      );
    }
  };

  // ── Subcomponents ─────────────────────────────────────────────────────

  const SaveBar = ({ onCancel, onSave }) => (
    <div className="flex gap-3 w-full sm:w-auto">
      <button
        onClick={onCancel}
        className="w-full sm:w-auto px-8 py-2 bg-[#0C0E12] hover:bg-gray-700 text-white rounded-lg border border-gray-700 transition-colors"
      >
        Cancel
      </button>
      <button
        onClick={onSave}
        className="w-full px-8 py-2 text-white rounded-lg font-medium transition-all duration-300 relative hover:scale-[1.02] hover:shadow-xl text-sm sm:text-base"
        style={{
          background:
            "radial-gradient(50% 50% at 50% 50%, #744EC5 0%, #5A3D9A 100%)",
          border: "1px solid",
          boxShadow:
            "0px 36px 32px -16px #7F56D936, 0px 8px 16px 0px #7F56D91F, 0px 2px 4px -8px #7F56D914, 0px 8px 24px -16px #FFFFFF3D inset, 0px -24px 32px 0px #FFFFFF38 inset, 0px 0px 12px 0px #FFFFFF inset",
        }}
      >
        Save
      </button>
    </div>
  );

  const SectionHeader = ({ title, description, count }) => (
    <div className="mb-6">
      <div className="flex items-center gap-3 mb-2">
        <h2 className="text-white text-lg font-medium">{title}</h2>
        {typeof count === "number" && (
          <span className="text-gray-400 text-sm">({count})</span>
        )}
      </div>
      {description && <p className="text-gray-400 text-sm">{description}</p>}
    </div>
  );

  const TableHeader = () => (
    <div className="flex items-center py-3 px-4 border-b border-gray-700">
      <div className="flex items-center flex-1">
        <span className="text-gray-400 text-sm font-medium">Name</span>
      </div>
      <div className="hidden md:flex w-24 mr-4">
        <span className="text-gray-400 text-sm font-medium">Access</span>
      </div>
      <div className="hidden md:block w-32">
        <span className="text-gray-400 text-sm font-medium">Date added</span>
      </div>
      <div className="hidden md:block w-32">
        <span className="text-gray-400 text-sm font-medium">Last active</span>
      </div>
      <div className="w-32" />
    </div>
  );

  const UserRow = ({ user, isAdminBadge = false }) => {
    const isCurrentUser = currentUser && user.id === currentUser.id;
    const [showRoleDropdown, setShowRoleDropdown] = useState(false);

    return (
      <div className="flex items-center py-4 px-4 hover:bg-gray-800/50 transition-colors">
        {/* Avatar + name */}
        <div className="flex items-center flex-1 min-w-0">
          <div className="relative mr-3">
            <img
              src={
                user.profileImageUrl ||
                `https://ui-avatars.com/api/?name=${user.name}&background=random`
              }
              alt={user.name}
              className="w-8 h-8 rounded-full object-cover"
            />
            {isAdminBadge && (
              <div className="absolute -top-1 -right-1 w-3 h-3 bg-purple-500 rounded-full border-2 border-[#0f1115]" />
            )}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <p className="text-white font-medium text-sm truncate">
                {user.name}
              </p>
              {isAdminBadge && (
                <span className="text-xs text-purple-400 font-medium">
                  Admin
                </span>
              )}
            </div>
            <p className="text-gray-400 text-xs truncate">{user.email}</p>
          </div>
        </div>

        {/* Access toggles */}
        <div className="hidden md:flex w-24 mr-4">
          {isAdmin && !isCurrentUser ? (
            <UserAccessCell user={user} onChanged={reloadTeam} />
          ) : (
            <div className="flex gap-1">
              {user.hasOctopushXAccess && (
                <span className="px-1.5 py-0.5 text-[10px] rounded bg-purple-600/20 text-purple-300 border border-purple-700/50">
                  X
                </span>
              )}
              {user.hasRecordingStudioAccess && (
                <span className="px-1.5 py-0.5 text-[10px] rounded bg-emerald-600/20 text-emerald-300 border border-emerald-700/50">
                  Studio
                </span>
              )}
            </div>
          )}
        </div>

        {/* Dates */}
        <div className="hidden md:block w-32 text-gray-400 text-sm">
          {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : "—"}
        </div>
        <div className="hidden md:block w-32 text-gray-400 text-sm">
          {user.lastLoginAt
            ? new Date(user.lastLoginAt).toLocaleDateString()
            : "Never"}
        </div>

        {/* Actions */}
        {isAdmin && !isCurrentUser ? (
          <div className="flex items-center gap-2 ml-4">
            <div className="relative">
              <button
                onClick={() => setShowRoleDropdown(!showRoleDropdown)}
                className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
              >
                <Edit className="w-4 h-4" />
              </button>
              {showRoleDropdown && (
                <div className="absolute right-0 mt-2 w-48 bg-[#1a1d23] border border-gray-600 rounded-lg shadow-lg z-20">
                  <button
                    onClick={() => {
                      handleUpdateRole(user.id, "admin");
                      setShowRoleDropdown(false);
                    }}
                    className="w-full text-left px-4 py-2 text-gray-300 hover:bg-gray-700 hover:text-white first:rounded-t-lg"
                  >
                    Make Admin
                  </button>
                  <button
                    onClick={() => {
                      handleUpdateRole(user.id, "member");
                      setShowRoleDropdown(false);
                    }}
                    className="w-full text-left px-4 py-2 text-gray-300 hover:bg-gray-700 hover:text-white"
                  >
                    Make Member
                  </button>
                </div>
              )}
            </div>
            <button
              onClick={() => handleRemoveUser(user.id)}
              className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <div className="w-20 ml-4" />
        )}
      </div>
    );
  };

  // ── Tab renderers ─────────────────────────────────────────────────────

  const renderMyDetails = () => {
    if (isLoadingUser) {
      return (
        <div className="rounded-lg pt-6">
          <div className="text-center py-8 text-gray-400">Loading...</div>
        </div>
      );
    }

    return (
      <div className="rounded-lg pt-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
          <div>
            <h2 className="text-white text-lg font-medium mb-1">
              Personal info
            </h2>
            <p className="text-gray-400 text-sm">
              Update your photo and personal details here.
            </p>
          </div>
          <SaveBar
            onCancel={() =>
              setPersonalDetails({
                name: currentUser?.name || "",
                email: currentUser?.email || "",
                profileImageUrl: currentUser?.profileImageUrl || "",
              })
            }
            onSave={handleUpdatePersonalDetails}
          />
        </div>

        <div className="space-y-6">
          <div className="flex flex-col sm:flex-row items-start gap-4 sm:gap-0 pb-8">
            <label className="block text-gray-300 text-sm mb-2 w-full sm:w-2/5">
              Name *
            </label>
            <div className="w-full sm:w-3/5">
              <input
                type="text"
                value={personalDetails.name}
                onChange={(e) =>
                  setPersonalDetails({
                    ...personalDetails,
                    name: e.target.value,
                  })
                }
                className="w-full bg-[#0C0E12] border border-gray-700 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-purple-500"
              />
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-start gap-4 sm:gap-0 pb-8">
            <label className="block text-gray-300 text-sm mb-2 w-full sm:w-2/5">
              Email address
            </label>
            <div className="w-full sm:w-3/5">
              <input
                type="email"
                value={personalDetails.email}
                onChange={(e) =>
                  setPersonalDetails({
                    ...personalDetails,
                    email: e.target.value,
                  })
                }
                className="w-full bg-[#0C0E12] border border-gray-700 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-purple-500"
              />
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-start gap-4 sm:gap-0 pb-8">
            <div className="w-full sm:w-2/5">
              <label className="block text-gray-300 text-sm mb-2">
                Your photo
              </label>
              <p className="text-gray-400 text-xs">
                This will be displayed on your profile.
              </p>
            </div>
            <div className="w-full sm:w-3/5">
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <img
                  src={
                    personalDetails.profileImageUrl ||
                    `https://ui-avatars.com/api/?name=${personalDetails.name}&background=random`
                  }
                  alt="Profile"
                  className="w-16 h-16 rounded-full object-cover"
                />
                <div className="border-2 border-dashed border-gray-600 rounded-lg p-4 sm:p-6 flex-1 max-w-md">
                  <input
                    type="file"
                    id="profile-upload"
                    className="hidden"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleProfileImageUpload(file);
                    }}
                  />
                  <label htmlFor="profile-upload" className="cursor-pointer">
                    <div className="text-center">
                      <Upload className="w-6 h-6 text-gray-400 mx-auto mb-2" />
                      <p className="text-white text-sm mb-1">
                        <span className="font-medium">Click to upload</span> or
                        drag and drop
                      </p>
                      <p className="text-gray-400 text-xs">
                        SVG, PNG or JPG (max. 800×400 px)
                      </p>
                    </div>
                  </label>
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-start gap-4 sm:gap-0 pb-8">
            <label className="block text-gray-300 text-sm mb-2 w-full sm:w-2/5">
              Role
            </label>
            <input
              type="text"
              value={currentUser?.role || ""}
              readOnly
              className="w-full sm:w-3/5 bg-[#0C0E12] border border-gray-700 rounded-lg px-3 py-2 text-gray-400 cursor-not-allowed"
            />
          </div>
        </div>
      </div>
    );
  };

  const renderWorkspace = () => {
    if (!isAdmin) {
      return (
        <div className="rounded-lg pt-6 text-center py-8 text-gray-400">
          You don't have permission to access this section.
        </div>
      );
    }

    if (isLoadingUser) {
      return (
        <div className="rounded-lg pt-6 text-center py-8 text-gray-400">
          Loading...
        </div>
      );
    }

    return (
      <div className="rounded-lg pt-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
          <div>
            <h2 className="text-white text-lg font-medium mb-1">Workspace</h2>
            <p className="text-gray-400 text-sm">
              Update your company logo and details here.
            </p>
          </div>
          <SaveBar
            onCancel={() => {
              if (currentUser?.company) {
                setWorkspaceDetails({
                  companyName: currentUser.company.name || "",
                  workspaceUrl: currentUser.company.workspaceUrl || "",
                  logoUrl: currentUser.company.logoUrl || "",
                });
              }
            }}
            onSave={handleUpdateWorkspaceDetails}
          />
        </div>

        <div className="space-y-6">
          <div className="flex flex-col sm:flex-row items-start gap-4 sm:gap-0 pb-8">
            <label className="block text-gray-300 text-sm mb-2 w-full sm:w-2/5">
              Company name
            </label>
            <div className="w-full sm:w-3/5">
              <input
                type="text"
                value={workspaceDetails.companyName}
                onChange={(e) =>
                  setWorkspaceDetails({
                    ...workspaceDetails,
                    companyName: e.target.value,
                  })
                }
                className="w-full bg-[#0C0E12] border border-gray-700 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-purple-500"
              />
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-start gap-4 sm:gap-0 pb-8">
            <label className="block text-gray-300 text-sm mb-2 w-full sm:w-2/5">
              Workspace URL
            </label>
            <div className="w-full sm:w-3/5">
              <input
                type="text"
                value={workspaceDetails.workspaceUrl}
                onChange={(e) =>
                  setWorkspaceDetails({
                    ...workspaceDetails,
                    workspaceUrl: e.target.value,
                  })
                }
                className="w-full bg-[#0C0E12] border border-gray-700 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-purple-500"
              />
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-start gap-4 sm:gap-0 pb-8">
            <div className="w-full sm:w-2/5">
              <label className="block text-gray-300 text-sm mb-2">
                Company Logo
              </label>
              <p className="text-gray-400 text-xs">Update your company logo.</p>
            </div>
            <div className="w-full sm:w-3/5">
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <img
                  src={
                    workspaceDetails.logoUrl ||
                    `https://ui-avatars.com/api/?name=${workspaceDetails.companyName}&background=random`
                  }
                  alt="Company Logo"
                  className="w-16 h-16 rounded-md object-cover"
                />
                <div className="border-2 border-dashed border-gray-600 rounded-lg p-4 sm:p-6 flex-1 max-w-md">
                  <input
                    type="file"
                    id="logo-upload"
                    className="hidden"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleCompanyLogoUpload(file);
                    }}
                  />
                  <label htmlFor="logo-upload" className="cursor-pointer">
                    <div className="text-center">
                      <Upload className="w-6 h-6 text-gray-400 mx-auto mb-2" />
                      <p className="text-white text-sm mb-1">
                        <span className="font-medium">Click to upload</span> or
                        drag and drop
                      </p>
                      <p className="text-gray-400 text-xs">
                        SVG, PNG or JPG (max. 800×400 px)
                      </p>
                    </div>
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderTeam = () => {
    if (!isAdmin) {
      return (
        <div className="rounded-lg pt-6 text-center py-8 text-gray-400">
          You don't have permission to access this section.
        </div>
      );
    }

    const adminUsers = teamMembers.filter(
      (u) => u.role === "admin" || u.role === "super_admin",
    );
    const regularUsers = teamMembers.filter((u) => u.role === "member");

    return (
      <div className="min-h-screen text-white pt-6">
        {/* License + seats overview */}
        <LicensePanel />

        {/* Admin users */}
        <div className="mb-12">
          <div className="flex justify-between items-center mb-6">
            <SectionHeader
              title="Admin users"
              description="Admins can add and remove users and manage organizational settings."
              count={adminUsers.length}
            />
            <button
              onClick={() => setShowInviteModal(true)}
              className="px-4 py-2 text-white rounded-lg font-medium transition-all duration-300 text-sm"
              style={{
                background:
                  "radial-gradient(50% 50% at 50% 50%, #744EC5 0%, #5A3D9A 100%)",
                border: "1px solid",
                boxShadow:
                  "0px 36px 32px -16px #7F56D936, 0px 8px 16px 0px #7F56D91F",
              }}
            >
              Invite Team Member
            </button>
          </div>

          {isLoadingTeam ? (
            <div className="text-center py-8">Loading...</div>
          ) : (
            <div className="bg-[#0C0E12] rounded-lg border border-gray-700">
              <TableHeader />
              <div className="divide-y divide-gray-700">
                {adminUsers.length === 0 ? (
                  <div className="py-8 text-center text-gray-500 text-sm">
                    No admins yet.
                  </div>
                ) : (
                  adminUsers.map((user) => (
                    <UserRow key={user.id} user={user} isAdminBadge />
                  ))
                )}
              </div>
            </div>
          )}
        </div>

        {/* Regular users */}
        <div className="mb-12">
          <SectionHeader
            title="Team members"
            description="Team members can access this workspace."
            count={regularUsers.length}
          />
          {isLoadingTeam ? (
            <div className="text-center py-8">Loading...</div>
          ) : (
            <div className="bg-[#0C0E12] rounded-lg border border-gray-700">
              <TableHeader />
              <div className="divide-y divide-gray-700">
                {regularUsers.length === 0 ? (
                  <div className="py-8 text-center text-gray-500 text-sm">
                    No team members yet.
                  </div>
                ) : (
                  regularUsers.map((user) => (
                    <UserRow key={user.id} user={user} />
                  ))
                )}
              </div>
            </div>
          )}
        </div>

        {/* Pending invitations */}
        <div>
          <SectionHeader
            title="Pending invitations"
            description="Invitations that haven't been accepted yet."
            count={pendingInvitations.length}
          />

          {isLoadingTeam ? (
            <div className="text-center py-8">Loading...</div>
          ) : pendingInvitations.length > 0 ? (
            <div className="bg-[#0C0E12] rounded-lg border border-gray-700">
              <div className="grid grid-cols-12 gap-4 py-3 px-4 border-b border-gray-700">
                <div className="col-span-4">
                  <span className="text-gray-400 text-sm font-medium">
                    Email
                  </span>
                </div>
                <div className="col-span-2">
                  <span className="text-gray-400 text-sm font-medium">
                    Role
                  </span>
                </div>
                <div className="col-span-2">
                  <span className="text-gray-400 text-sm font-medium">
                    Products
                  </span>
                </div>
                <div className="col-span-2">
                  <span className="text-gray-400 text-sm font-medium">
                    Expires
                  </span>
                </div>
                <div className="col-span-2" />
              </div>
              <div className="divide-y divide-gray-700">
                {pendingInvitations.map((invite) => (
                  <div
                    key={invite.id}
                    className="grid grid-cols-12 gap-4 py-4 px-4"
                  >
                    <div className="col-span-4">
                      <p className="text-white truncate">{invite.email}</p>
                    </div>
                    <div className="col-span-2">
                      <span className="capitalize text-gray-400">
                        {invite.role}
                      </span>
                    </div>
                    <div className="col-span-2 flex gap-1 items-center">
                      {invite.grantOctopushXAccess && (
                        <span className="px-1.5 py-0.5 text-[10px] rounded bg-purple-600/20 text-purple-300 border border-purple-700/50">
                          X
                        </span>
                      )}
                      {invite.grantRecordingStudioAccess && (
                        <span className="px-1.5 py-0.5 text-[10px] rounded bg-emerald-600/20 text-emerald-300 border border-emerald-700/50">
                          Studio
                        </span>
                      )}
                    </div>
                    <div className="col-span-2">
                      <span className="text-gray-400">
                        {invite.expiresAt
                          ? new Date(invite.expiresAt).toLocaleDateString()
                          : "—"}
                      </span>
                    </div>
                    <div className="col-span-2 flex justify-end gap-2">
                      <button
                        onClick={() => handleResendInvitation(invite.id)}
                        className="text-purple-400 hover:text-purple-300 text-sm"
                      >
                        Resend
                      </button>
                      <button
                        onClick={() => handleCancelInvitation(invite.id)}
                        className="text-red-400 hover:text-red-300 text-sm"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-400">
              No pending invitations
            </div>
          )}
        </div>

        {/* Invite modal */}
        <InviteTeamModal
          open={showInviteModal}
          onClose={() => setShowInviteModal(false)}
          onInvited={reloadTeam}
        />
      </div>
    );
  };

  const renderVoicePreferences = () => (
    <div className="min-h-screen text-white pt-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <div>
          <h2 className="text-white text-lg font-medium mb-1">
            Voice preferences
          </h2>
          <p className="text-gray-400 text-sm">
            Defaults applied when you create new voice projects.
          </p>
        </div>
        <SaveBar
          onCancel={() => {
            setDefaultVoice("Jenny (en-GB)");
            setDefaultTone("Friendly");
            setDefaultModel("Studio v2");
            setEnableMusicLayer(true);
            setEnableAutoNormalize(true);
          }}
          onSave={() => toast.success("Voice preferences saved")}
        />
      </div>

      <div className="space-y-8">
        {/* Default voice */}
        <div className="flex flex-col sm:flex-row gap-4 sm:gap-8">
          <div className="flex-1">
            <h3 className="text-white font-medium mb-1">Default voice</h3>
            <p className="text-gray-400 text-sm">
              Used as the first voice when starting a new project.
            </p>
          </div>
          <div className="flex-1">
            <Dropdown
              isOpen={isVoiceOpen}
              setIsOpen={setIsVoiceOpen}
              value={defaultVoice}
              options={voices}
              onChange={setDefaultVoice}
              placeholder="Select voice"
            />
          </div>
        </div>

        {/* Default tone */}
        <div className="flex flex-col sm:flex-row gap-4 sm:gap-8">
          <div className="flex-1">
            <h3 className="text-white font-medium mb-1">Default tone</h3>
            <p className="text-gray-400 text-sm">
              Applied to new blocks unless overridden.
            </p>
          </div>
          <div className="flex-1">
            <Dropdown
              isOpen={isToneOpen}
              setIsOpen={setIsToneOpen}
              value={defaultTone}
              options={tones}
              onChange={setDefaultTone}
              placeholder="Select tone"
            />
          </div>
        </div>

        {/* Default model */}
        <div className="flex flex-col sm:flex-row gap-4 sm:gap-8">
          <div className="flex-1">
            <h3 className="text-white font-medium mb-1">Synthesis model</h3>
            <p className="text-gray-400 text-sm">
              Higher tiers give better quality but consume more credits.
            </p>
          </div>
          <div className="flex-1">
            <Dropdown
              isOpen={isModelOpen}
              setIsOpen={setIsModelOpen}
              value={defaultModel}
              options={models}
              onChange={setDefaultModel}
              placeholder="Select model"
            />
          </div>
        </div>

        {/* Music layer */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-0">
          <div className="flex-1">
            <h3 className="text-white font-medium mb-1 flex items-center gap-2">
              <Music className="w-4 h-4 text-violet-400" /> Auto-enable music
              layer
            </h3>
            <p className="text-gray-400 text-sm">
              New projects will start with a background music track slot ready.
            </p>
          </div>
          <Toggle enabled={enableMusicLayer} onChange={setEnableMusicLayer} />
        </div>

        {/* Auto normalize */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-0">
          <div className="flex-1">
            <h3 className="text-white font-medium mb-1">
              Auto-normalize audio
            </h3>
            <p className="text-gray-400 text-sm">
              Level block volumes automatically on render.
            </p>
          </div>
          <Toggle
            enabled={enableAutoNormalize}
            onChange={setEnableAutoNormalize}
          />
        </div>
      </div>
    </div>
  );

  // ── Router ────────────────────────────────────────────────────────────
  const renderTabContent = () => {
    switch (activeTab) {
      case "my-details":
        return renderMyDetails();
      case "workspace":
        return renderWorkspace();
      case "team":
        return renderTeam();
      case "voice-preferences":
        return renderVoicePreferences();
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-[#0C0E12] text-white">
      <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6">
          <h1 className="text-2xl font-semibold mb-4 sm:mb-0">Settings</h1>
        </div>

        <div className="mb-6 overflow-x-auto">
          <nav className="flex space-x-6 sm:space-x-8 min-w-max">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-3 text-sm font-medium transition-colors whitespace-nowrap ${
                  activeTab === tab.id
                    ? "text-[#CECFD2] bg-[#22262F] px-3 py-2 rounded-lg"
                    : "text-gray-400 hover:text-gray-300 px-3 py-2"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {renderTabContent()}
      </div>
    </div>
  );
}

export default SettingsPage;
